import React, { useImperativeHandle, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Box,
    CircularProgress,
    Typography
} from '@mui/material';
import { useSettingsStore } from '@core/store';
import { useTranslation } from 'react-i18next';
import { WcnButton, WcnTextField } from '@/components';

export interface DeleteConfirmationDialogProps {
    // Content props
    expectedInputText: string;
    heading?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
    icon?: string | React.ReactNode;

    // Control props
    isDeleting?: boolean;
    waitForConfirm?: boolean;

    // Event handlers
    onConfirm?: () => void | Promise<void>;
    onClose?: () => void;
}

export interface DeleteConfirmationDialogRef {
    openDialog: () => void;
}

type Props = DeleteConfirmationDialogProps & {
    dialogRef?: React.Ref<DeleteConfirmationDialogRef>;
};

const DeleteConfirmationDialogRequireInput = ({
    // Content props
    heading = '',
    message = '',
    confirmText = '',
    cancelText = '',
    expectedInputText = '',
    icon,

    // Control props
    isDeleting = false,
    waitForConfirm = false,

    // Event handlers
    onConfirm,
    onClose,

    // Ref prop
    dialogRef
}: Props) => {
    const [open, setOpen] = useState(false);
    const { isMobile } = useSettingsStore();
    const { t } = useTranslation();
    const [confirmationText, setConfirmationText] = useState('');

    const isConfirmDisabled = confirmationText !== expectedInputText;
    const hasError = confirmationText !== '' && confirmationText !== expectedInputText;

    useImperativeHandle(dialogRef, () => ({
        openDialog: () => {
            setConfirmationText('');
            setOpen(true);
        }
    }));

    const handleConfirm = async () => {
        if (isConfirmDisabled) return;
        if (!waitForConfirm) {
            onConfirm?.();
            setOpen(false);
            return;
        }

        await onConfirm?.();
        setOpen(false);
        setConfirmationText('');
    };

    const handleClose = () => {
        onClose?.();
        setOpen(false);
        setConfirmationText('');
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth={isMobile}
            fullScreen={false}
            className="dialog-container"
            PaperProps={{
                sx: { borderRadius: '3px !important', minWidth: isMobile ? 'auto' : '600px', maxWidth: '900px' }
            }}
        >
            <Box sx={{ p: isMobile ? 1 : '24px 64px' }}>
                <DialogTitle
                    sx={{
                        textAlign: 'center',
                        fontWeight: 400,
                        fontSize: '1rem',
                        letterSpacing: '1.4px',
                        textTransform: 'uppercase',
                        pt: 4
                    }}
                >
                    {heading}
                </DialogTitle>

                <Box className="dialog-icon">
                    {icon ?? (
                        <i
                            className="bi bi-exclamation-triangle warning-icon"
                            style={{ color: 'var(--color-warning)' }}
                        />
                    )}
                </Box>

                <DialogContent>
                    <DialogContentText>
                        <Typography
                            sx={{
                                textAlign: 'center',
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-line',
                                fontSize: '14px',
                                pb: '40px'
                            }}
                        >
                            {message}
                        </Typography>
                    </DialogContentText>

                    <WcnTextField
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        label={t('typeToConfirmName', { name: expectedInputText })}
                        placeholder={expectedInputText}
                        variant="outlined"
                        size="small"
                        fullWidth
                        error={hasError}
                        autoComplete="off"
                        helperText={hasError ? t('mustTypeNameToConfirm', { name: expectedInputText }) : ''}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'center' }}>
                    <WcnButton onClick={handleClose} variant="primary" style={{ flex: 1 }}>
                        {cancelText ? t(cancelText) : t('no')}
                    </WcnButton>

                    <WcnButton onClick={handleConfirm} disabled={isConfirmDisabled} variant="red" style={{ flex: 1 }}>
                        {isDeleting ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                                <CircularProgress size={14} />
                                <span>{confirmText ? t(confirmText) : t('delete')}</span>
                            </Box>
                        ) : confirmText ? (
                            t(confirmText)
                        ) : (
                            t('delete')
                        )}
                    </WcnButton>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

DeleteConfirmationDialogRequireInput.displayName = 'DeleteConfirmationDialogRequireInput';

export default DeleteConfirmationDialogRequireInput;
